import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { User } from '@supabase/supabase-js';
import LoginPage from './pages/LoginPage';
import ProductsPage from './pages/ProductsPage';
import {
    LogOut, LayoutDashboard, Package, Settings,
    Users, Loader2, LayoutTemplate, Menu, X,
    Store, ChevronDown, MessageCircle, Book, Video
} from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { dashboardService } from '../services/DashboardService';
import ContentPage from './pages/ContentPage';
import SettingsPage from './pages/SettingsPage';
import DocsPage from './pages/DocsPage';
import AdminReviewsPage from './pages/AdminReviewsPage';
import KPITimeseriesChart from './components/KPITimeseriesChart';
import { reviewService } from '../services/ReviewService';
import { useToast } from '../context/ToastContext';

import AdminSocialPage from './pages/AdminSocialPage';

type AdminPage = 'dashboard' | 'products' | 'settings' | 'content' | 'docs' | 'reviews' | 'social';

const AdminLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // Estado extendido para incluir rol y nombre
    const [userProfile, setUserProfile] = useState<{ email: string, full_name: string | null, role: string } | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activePage, setActivePage] = useState<AdminPage>(() => {
        const saved = localStorage.getItem('admin_active_page');
        return (saved as AdminPage) || 'dashboard';
    });
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isCreatingProduct, setIsCreatingProduct] = useState(false);
    const { showToast } = useToast();

    // Stats (se mantienen igual)
    // Stats extendidas
    const [stats, setStats] = useState<{
        totalProducts: number;
        whatsappQueries: number;
        activeBranches: number;
        queriesChange: string;
        queriesByBranch: { name: string, count: number, phones: { number: string, count: number }[] }[];
        topProducts: { id: string, name: string, count: number, branches: { name: string, count: number }[] }[];
    }>({
        totalProducts: 0,
        whatsappQueries: 0,
        activeBranches: 0,
        queriesChange: "",
        queriesByBranch: [],
        topProducts: []
    });

    const [expandedBranch, setExpandedBranch] = useState<string | null>(null);
    const [expandedProduct, setExpandedProduct] = useState<string | null>(null);

    const loadUserProfile = async () => {
        const { data: { session } } = await supabase.auth.getSession();

        if (session?.user) {
            // Buscamos datos extra en tabla profiles
            const { data: profile } = await supabase
                .from('profiles')
                .select('full_name, role')
                .eq('id', session.user.id)
                .single();

            const fetchedRole = profile?.role || 'admin';

            setUserProfile({
                email: session.user.email || '',
                full_name: profile?.full_name || null,
                role: fetchedRole
            });

            // Redirect non-admins away from dashboard
            if (fetchedRole !== 'admin' && activePage === 'dashboard') {
                setActivePage('products');
            }
        } else {
            setUserProfile(null);
        }
        setIsLoading(false);
    };

    // Cargar Estadísticas
    const loadStats = async () => {
        try {
            const data = await dashboardService.getStats();
            setStats({
                totalProducts: data.totalProducts,
                whatsappQueries: data.whatsappQueries,
                activeBranches: data.activeBranches,
                queriesChange: data.queriesChange || "",
                queriesByBranch: data.queriesByBranch,
                topProducts: data.topProducts
            });
        } catch (error) {
            console.error('Error loading dashboard stats:', error);
        }
    };

    useEffect(() => {
        localStorage.setItem('admin_active_page', activePage);
    }, [activePage]);

    useEffect(() => {
        let channel: any = null;
        let isMounted = true;

        const initAdmin = async () => {
            await loadUserProfile();
            if (!isMounted) return;
            loadStats();

            // Realtime setup
            const { data: { session } } = await supabase.auth.getSession();
            if (session && isMounted) {
                // Pequeño retraso para asegurar que el token de auth esté listo en el cliente realtime
                await new Promise(r => setTimeout(r, 500));
                if (!isMounted) return;

                const channelId = `admin-logs-${Math.random().toString(36).substring(2, 7)}`;
                channel = supabase
                    .channel(channelId)
                    .on(
                        'postgres_changes',
                        {
                            event: 'INSERT',
                            schema: 'public',
                            table: 'whatsapp_logs'
                        },
                        () => {
                            if ((import.meta as any).env.DEV) console.log(`[Realtime Admin] New log detected, refreshing stats...`);
                            loadStats();
                        }
                    )
                    .subscribe((status, err) => {
                        if (!isMounted) return;
                        if (status === 'SUBSCRIBED') {
                            if ((import.meta as any).env.DEV) console.log(`[Realtime Admin] ✅ Connected: ${channelId}`);
                        }
                        if (status === 'CHANNEL_ERROR') {
                            // Supabase Realtime a veces reporta CHANNEL_ERROR momentáneamente al reconectar
                            // Solo reportamos si hay un error real y persistente
                            if ((import.meta as any).env.DEV && err) {
                                console.warn(`[Realtime Admin] ⚠️ Error de suscripción:`, err);
                            }
                        }
                    });
            }
        };

        initAdmin();

        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            if (isMounted) {
                if (session) {
                    loadUserProfile();
                } else {
                    setUserProfile(null);
                }
            }
        });

        const pollingInterval = setInterval(() => {
            if (isMounted) loadStats();
        }, 60000);

        return () => {
            isMounted = false;
            subscription.unsubscribe();
            if (channel) supabase.removeChannel(channel);
            clearInterval(pollingInterval);
        };
    }, []);

    const handleLogout = async () => {
        await supabase.auth.signOut();
        window.location.reload();
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <Loader2 className="h-10 w-10 text-white animate-spin" />
            </div>
        );
    }

    if (!userProfile) {
        return <LoginPage />;
    }

    // Nombre a mostrar: Full Name > Email > 'Usuario'
    const displayName = userProfile.full_name || userProfile.email.split('@')[0];
    const userRole = userProfile.role || 'admin';

    return (
        <div className="h-screen bg-black text-white flex flex-col md:flex-row overflow-hidden">
            {/* Mobile Header */}
            <div className="md:hidden bg-zinc-900 border-b border-zinc-900 p-4 flex justify-between items-center sticky top-0 z-40">
                <img
                    src="/CCSV_LOGO.svg"
                    alt="CCSV Logo"
                    className="h-8 w-auto object-contain"
                />
                <button
                    onClick={() => setIsSidebarOpen(true)}
                    className="p-2 text-zinc-400 hover:text-white"
                >
                    <Menu size={24} />
                </button>
            </div>

            {/* Sidebar Overlay for Mobile */}
            {isSidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40 md:hidden"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside className={`
                fixed inset-y-0 left-0 z-50 w-64 bg-zinc-950/95 backdrop-blur-xl border-r border-zinc-900 flex flex-col transition-transform duration-300 ease-out
                md:translate-x-0 md:sticky md:top-0 md:bg-transparent md:backdrop-blur-none md:h-screen md:shrink-0
                ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            `}>
                <div className="p-6 border-b border-zinc-900 flex justify-between items-center relative">
                    <button
                        onClick={() => setActivePage(userRole === 'admin' ? 'dashboard' : 'products')}
                        className="w-full flex justify-center py-4 transition-all duration-300 hover:drop-shadow-[0_0_15px_rgba(255,255,255,0.4)] active:scale-95 cursor-pointer group"
                    >
                        <img
                            src="/CCSV_LOGO.svg"
                            alt="CCSV Admin"
                            className="h-12 w-auto object-contain transition-transform group-hover:scale-105"
                        />
                    </button>
                    {/* Close button - Mobile only */}
                    <button
                        onClick={() => setIsSidebarOpen(false)}
                        className="md:hidden text-zinc-500 hover:text-white"
                    >
                        <X size={24} />
                    </button>
                </div>

                <nav className="flex-grow p-4 space-y-2 overflow-y-auto">
                    {userRole === 'admin' && (
                        <SidebarLink
                            icon={<LayoutDashboard size={20} />}
                            label="Dashboard"
                            active={activePage === 'dashboard'}
                            onClick={() => { setActivePage('dashboard'); setIsSidebarOpen(false); }}
                        />
                    )}
                    <SidebarLink
                        icon={<Package size={20} />}
                        label="Productos"
                        active={activePage === 'products'}
                        onClick={() => { setActivePage('products'); setIsSidebarOpen(false); }}
                    />
                    <SidebarLink
                        icon={<LayoutTemplate size={20} />}
                        label="Contenido"
                        active={activePage === 'content'}
                        onClick={() => { setActivePage('content'); setIsSidebarOpen(false); }}
                    />
                    {userRole === 'admin' && (
                        <SidebarLink
                            icon={<Video size={20} />}
                            label="Social"
                            active={activePage === 'social'}
                            onClick={() => { setActivePage('social'); setIsSidebarOpen(false); }}
                        />
                    )}

                    {userRole === 'admin' && (
                        <SidebarLink
                            icon={<Book size={20} />}
                            label="Documentación"
                            active={activePage === 'docs'}
                            onClick={() => { setActivePage('docs'); setIsSidebarOpen(false); }}
                        />
                    )}

                    <SidebarLink
                        icon={<Settings size={20} />}
                        label="Configuración"
                        active={activePage === 'settings'}
                        onClick={() => { setActivePage('settings'); setIsSidebarOpen(false); }}
                    />


                </nav>

                <div className="p-4 border-t border-zinc-900">
                    <div className="flex items-center gap-3 p-3 bg-zinc-900/50 mb-4 rounded-sm">
                        <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center text-black font-bold text-xs">
                            {userProfile.email[0].toUpperCase()}
                        </div>
                        <div className="overflow-hidden">
                            <p className="text-xs font-bold truncate">{displayName}</p>
                            <p className="text-[10px] text-zinc-500 uppercase">{userRole}</p>
                        </div>
                    </div>
                    <button
                        onClick={handleLogout}
                        className="w-full flex items-center justify-center gap-2 p-3 text-red-500 hover:bg-red-500/10 transition-colors rounded-sm uppercase font-mono text-xs font-bold tracking-widest"
                    >
                        <LogOut size={16} /> Salir
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-grow overflow-auto p-4 md:p-10">
                <header className="mb-10 flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4">
                    <div className="flex-grow">
                        <h1 className="text-3xl font-display font-bold uppercase italic">
                            {activePage === 'dashboard' ? 'Bienvenido, ' : ''}
                            <span className="text-white">
                                {activePage === 'dashboard'
                                    ? (userProfile.full_name?.split(' ')[0] || userProfile.email.split('@')[0])
                                    : activePage === 'products' ? 'PRODUCTOS'
                                        : activePage === 'content' ? 'CONTENIDO'
                                            : activePage === 'settings' ? 'CONFIGURACIÓN'
                                                : activePage === 'docs' ? 'DOCUMENTACIÓN'
                                                    : activePage === 'social' ? 'SOCIAL'
                                                        : activePage.toUpperCase()}
                            </span>
                        </h1>
                        <p className="text-zinc-500 text-sm mt-1">Panel de control de Carbon Case SV</p>
                    </div>
                    {/* Desktop Actions */}
                    <div className="hidden md:flex items-center gap-4">
                        {activePage === 'products' && (userRole === 'admin' || userRole === 'marketing') && (
                            <button
                                onClick={() => setIsCreatingProduct(true)}
                                className="bg-white text-black px-6 py-2 rounded-sm font-bold uppercase text-xs tracking-widest hover:bg-zinc-200 transition-all active:scale-95 shadow-[0_0_15px_rgba(255,255,255,0.3)] hover:shadow-[0_0_20px_rgba(255,255,255,0.4)] flex items-center gap-2"
                            >
                                <Package size={16} /> Añadir Artículo
                            </button>
                        )}
                    </div>


                </header>

                {activePage === 'dashboard' && userRole === 'admin' && (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                            <StatCard
                                label="Total de Artículos"
                                value={stats.totalProducts.toLocaleString()}
                                icon={Package}
                            />
                            <StatCard
                                label="Consultas WhatsApp"
                                value={stats.whatsappQueries.toLocaleString()}
                                change={stats.queriesChange}
                                changeLabel="vs ayer"
                                icon={FaWhatsapp}
                                showSparkline={true}
                            />
                        </div>

                        {/* KPI Timeseries Module */}
                        <KPITimeseriesChart />

                        {/* Breakdown Section */}
                        <div className="grid grid-cols-1 gap-8 mb-10">
                            {/* Top Products - Full Width */}
                            <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
                                <div className="p-4 border-b border-zinc-800 bg-zinc-950/50 flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="text-white flex items-center">
                                            <FaWhatsapp size={14} />
                                        </span>
                                        <h3 className="text-xs font-mono font-bold uppercase tracking-widest text-zinc-400">Top Productos Consultados</h3>
                                    </div>
                                    <Package size={14} className="text-zinc-600" />
                                </div>
                                <div className="divide-y divide-zinc-800/50">
                                    {stats.topProducts.map((product, i) => {
                                        const maxCount = stats.topProducts[0]?.count || 1;
                                        const percentage = (product.count / maxCount) * 100;

                                        return (
                                            <div key={i} className="flex flex-col relative overflow-hidden group/prod">
                                                {/* Sparkline / Progress Bar */}
                                                <div
                                                    className="absolute left-0 top-0 bottom-0 bg-white/5 z-0 transition-all duration-500 ease-out"
                                                    style={{ width: `${percentage}%` }}
                                                />
                                                <div
                                                    className="p-3.5 flex items-center justify-between hover:bg-zinc-800/30 transition-colors relative z-10"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <span className="text-[10px] font-mono text-zinc-700 w-4">{i + 1}</span>
                                                        <span className="text-xs font-bold text-zinc-300 truncate max-w-[200px] md:max-w-none group-hover/prod:text-white transition-colors">{product.name}</span>
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                        <span className="text-[10px] px-2 py-0.5 bg-white/10 text-white border border-white/20 rounded-full font-bold">
                                                            {product.count} INTENCIONES
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {stats.topProducts.length === 0 && (
                                        <p className="text-zinc-600 text-[10px] italic text-center py-8">No hay productos registrados aún.</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}



                {activePage === 'products' && <ProductsPage isCreating={isCreatingProduct} onSetIsCreating={setIsCreatingProduct} userRole={userRole} />}
                {activePage === 'content' && <ContentPage />}

                {activePage === 'settings' && <SettingsPage onProfileUpdate={loadUserProfile} />}
                {activePage === 'docs' && <DocsPage />}
                {activePage === 'reviews' && <AdminReviewsPage />}
                {activePage === 'social' && <AdminSocialPage />}


            </main>
        </div>
    );
};

const SidebarLink: React.FC<{ icon: React.ReactNode, label: string, active?: boolean, onClick: () => void }> = ({ icon, label, active, onClick }) => (
    <button
        onClick={onClick}
        className={`w-full flex items-center gap-3 p-3 transition-all rounded-sm font-mono text-xs uppercase tracking-widest font-bold ${active ? 'bg-white text-black shadow-[0_0_15px_rgba(255,255,255,0.3)]' : 'text-zinc-500 hover:text-white hover:bg-zinc-900 text-left'}`}
    >
        {icon} {label}
    </button>
);

const StatCard: React.FC<{
    label: string,
    value: string,
    change?: string,
    changeLabel?: string,
    icon: any,
    status?: 'isOpen' | 'isClosed',
    showSparkline?: boolean
}> = ({ label, value, change, changeLabel, icon: Icon, status, showSparkline }) => {
    // Determinar la forma de la gráfica según la tendencia
    let pathArea = "M0 20 L 0 20 C 20 10, 40 18, 60 8 S 80 15, 100 0 L 100 20 Z"; // Por defecto: subiendo
    let pathLine = "M0 20 C 20 10, 40 18, 60 8 S 80 15, 100 0";

    if (change) {
        if (change === "0%" || change === "+0%" || change === "-0%") {
            // Tendencia plana / neutral (completamente plana en Y = 18)
            pathArea = "M0 20 L 0 18 L 100 18 L 100 20 Z";
            pathLine = "M0 18 L 100 18";
        } else if (change.startsWith("-")) {
            // Tendencia bajando
            pathArea = "M0 20 L 0 5 C 20 10, 40 2, 60 12 S 80 5, 100 20 L 100 20 Z";
            pathLine = "M0 5 C 20 10, 40 2, 60 12 S 80 5, 100 20";
        }
    }

    return (
        <div className="bg-zinc-900 border border-zinc-800 p-6 relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-2 opacity-10 z-0">
                <Icon size={80} />
            </div>

            {/* Mini-chart decorativo simulando tendencia */}
            {showSparkline && (
                <svg
                    className="absolute bottom-0 left-0 w-full h-1/2 opacity-20 pointer-events-none z-0 text-white transition-all duration-500"
                    preserveAspectRatio="none"
                    viewBox="0 0 100 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d={pathArea}
                        fill="currentColor"
                        className="opacity-10"
                    />
                    <path
                        d={pathLine}
                        stroke="currentColor"
                        strokeWidth="1"
                        strokeLinecap="round"
                        className="drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]"
                    />
                </svg>
            )}

            <div className="relative z-10">
                <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest mb-1">{label}</p>
                <div className="flex items-center gap-3">
                    <h4 className="text-4xl font-display font-black text-white">{value}</h4>
                    {status && (
                        <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-tighter ${status === 'isOpen'
                            ? 'bg-white/10 text-white border-white/20'
                            : 'bg-red-500/10 text-red-500 border-red-500/20 opacity-60'
                            }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${status === 'isOpen' ? 'bg-white animate-pulse' : 'bg-red-500'}`}></span>
                            {status === 'isOpen' ? 'Abierto' : 'Cerrado'}
                        </div>
                    )}
                    {change && (
                        <div className="flex flex-col">
                            <span className="text-white text-xs font-bold">{change}</span>
                            {changeLabel && <span className="text-zinc-500 text-[9px] uppercase tracking-wider">{changeLabel}</span>}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};


export default AdminLayout;
